<template>
  <component :is="wrapper || 'div'" v-bind="props">
    <div class="slider-filter px-4 pb-4">
      <div class="px-4 py-5">
        <slider
          :id="`slider-${filter.key}`"
          :aria-labelledby="`slider-${filter.key}`"
          :model-value="modelValue"
          :min="min"
          :max="max"
          :step="-1"
          :tooltips="false"
          :name="`slider-${filter.key}`"
          @update:model-value="onModelValueChanged($event)"
          @slide="onSliderDrag"
        />
      </div>
      <div class="mt-5 flex gap-2">
        <div class="flex-1">
          <label :for="`${filter.key}-from-${min}-${max}`">
            {{ $t("From") }}
          </label>
          <input
            :id="`${filter.key}-from-${min}-${max}`"
            v-model="from"
            class="w-full min-w-0 px-4 py-2 ring-1 ring-zinc-300"
            :name="`slider-from-${filter.key}`"
            :min="min"
            :max="max"
            type="number"
            pattern="[0-9]*"
            inputmode="decimal"
            @blur="setValueFromInput('from')"
          />
        </div>
        <div class="flex-1">
          <label :for="`${filter.key}-to-${min}-${max}`">{{ $t("To") }}</label>
          <input
            :id="`${filter.key}-to-${min}-${max}`"
            v-model="to"
            class="w-full min-w-0 px-4 py-2 ring-1 ring-zinc-300"
            :name="`slider-to-${filter.key}`"
            :min="min"
            :max="max"
            type="number"
            pattern="[0-9]*"
            inputmode="decimal"
            @blur="setValueFromInput('to')"
          />
        </div>
      </div>
    </div>
  </component>
</template>
<script lang="ts" setup>
// eslint-disable-next-line import/default
import Slider from "@vueform/slider";
import type { KlevuFilterResultSlider } from "@klevu/core";
import { isEqual } from "lodash-es";
import type { Component } from "vue";
import type { FilterManagerFilterNumber } from "~/modules/catalog/classes/FilterManager/FilterManagerFilterNumber";
import type { IFilterManager } from "~/modules/catalog/classes/IFilterManager";

const props = defineProps<{
  wrapper?: Component;
  filter: KlevuFilterResultSlider | FilterManagerFilterNumber;
  filterManager: IFilterManager;
}>();

const emit = defineEmits<{
  (_event: "filterChanged", _param: any): void;
}>();

const min = computed<number>(() => parseFloat(props.filter.min));
const max = computed<number>(() => parseFloat(props.filter.max));
const minMaxModel = computed<number[]>(() => [min.value, max.value]);
const modelValue = ref([
  props.filter.start ? parseFloat(props.filter.start) || min.value : min.value,
  props.filter.end ? parseFloat(props.filter.end) || max.value : max.value,
]);

const from = ref<number>(modelValue.value[0]);
const to = ref<number>(modelValue.value[1]);

function updateFilterNumber(
  key: string,
  minVal: number | string,
  maxVal: number | string,
): void {
  if (props.filterManager.updateSlide) {
    props.filterManager.updateSlide(key, minVal, maxVal);
  } else if (props.filterManager.updateNumber) {
    props.filterManager.updateNumber(key, minVal, maxVal);
  }
}

function setValueFromInput(value: "to" | "from"): void {
  if (value === "from") {
    modelValue.value[0] = Math.max(
      min.value,
      Math.min(from.value, to.value - 1),
    );
  } else {
    modelValue.value[1] = Math.min(
      max.value,
      Math.max(from.value + 1, to.value),
    );
  }

  onModelValueChanged(modelValue.value.slice());
}

function onModelValueChanged(v: number[]): void {
  modelValue.value = v;
  from.value = v[0];
  to.value = v[1];

  if (isEqual(minMaxModel.value, v)) {
    updateFilterNumber(props.filter.key, "" as any, "" as any);
  } else {
    updateFilterNumber(props.filter.key, v[0], v[1]);
  }

  emit("filterChanged", props.filter);
}

function onSliderDrag(v: number[]): void {
  from.value = v[0];
  to.value = v[1];
}
</script>
<style src="@vueform/slider/themes/default.css"></style>
<style lang="scss">
.slider-filter {
  .slider-target {
    --slider-height: 0.25rem;
    --slider-radius: 0;
    --slider-handle-height: 2.5rem;
    --slider-handle-width: 2.5rem;

    .slider-handle {
      @apply border-[1px] border-solid border-secondary-600 shadow-none;
    }

    .slider-connects {
      @apply bg-zinc-200;
      .slider-connect {
        @apply bg-secondary-600;
      }
    }
  }
}
</style>
